import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["componentCheckbox", "selectedCount", "addButton", "componentGrid", "reorderList", "componentCard"]
  
  connect() {
    this.updateSelectedCount()
    this.setupTabSwitching()
  }
  
  setupTabSwitching() {
    // Update footer content when tabs switch
    const tabs = this.element.querySelectorAll('[data-bs-toggle="tab"]')
    tabs.forEach(tab => {
      tab.addEventListener('shown.bs.tab', (e) => {
        const targetId = e.target.getAttribute('data-bs-target')
        this.updateFooterForTab(targetId)
      })
    })
  }
  
  updateFooterForTab(targetId) {
    const footers = this.element.querySelectorAll('.modal-footer .tab-pane')
    footers.forEach(footer => footer.classList.remove('show', 'active'))
    
    if (targetId === '#addComponentsTab') {
      const addFooter = this.element.querySelector('#addComponentsFooter')
      if (addFooter) {
        addFooter.classList.add('show', 'active')
      }
    } else if (targetId === '#reorderComponentsTab') {
      const reorderFooter = this.element.querySelector('#reorderComponentsFooter')
      if (reorderFooter) {
        reorderFooter.classList.add('show', 'active')
      }
    }
  }
  
  // Multi-select functionality
  componentCheckboxTargetConnected(element) {
    element.addEventListener('change', () => {
      this.updateSelectedCount()
      this.toggleComponentCard(element)
    })
  }
  
  toggleComponentCard(checkbox) {
    const card = checkbox.closest('[data-component-manager-target="componentCard"]')
    if (checkbox.checked) {
      card.classList.add('border-primary', 'bg-light')
    } else {
      card.classList.remove('border-primary', 'bg-light')
    }
  }
  
  updateSelectedCount() {
    const checkedBoxes = this.componentCheckboxTargets.filter(cb => cb.checked)
    const count = checkedBoxes.length
    
    this.selectedCountTarget.textContent = `${count} component${count !== 1 ? 's' : ''} selected`
    this.addButtonTarget.disabled = count === 0
  }
  
  selectAll() {
    this.componentCheckboxTargets.forEach(checkbox => {
      checkbox.checked = true
      this.toggleComponentCard(checkbox)
    })
    this.updateSelectedCount()
  }
  
  clearSelection() {
    this.componentCheckboxTargets.forEach(checkbox => {
      checkbox.checked = false
      this.toggleComponentCard(checkbox)
    })
    this.updateSelectedCount()
  }
  
  async addSelectedComponents() {
    const selectedTemplateFiles = this.componentCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value)
    
    if (selectedTemplateFiles.length === 0) return
    
    try {
      this.addButtonTarget.disabled = true
      this.addButtonTarget.innerHTML = '<i class="bi bi-hourglass me-1"></i>Adding...'
      
      const response = await fetch(`/admin/pages/${this.getPageId()}/components/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ template_files: selectedTemplateFiles })
      })
      
      if (response.ok) {
        // Reload the page to show the new components
        window.location.reload()
      } else {
        throw new Error('Failed to add components')
      }
    } catch (error) {
      console.error('Error adding components:', error)
      alert('Failed to add components. Please try again.')
      this.addButtonTarget.disabled = false
      this.addButtonTarget.innerHTML = '<i class="bi bi-plus me-1"></i>Add Selected Components'
    }
  }
  
  async removeComponent(event) {
    const componentId = event.currentTarget.dataset.componentId
    const confirmMessage = event.currentTarget.dataset.confirm
    
    if (!confirm(confirmMessage)) return
    
    try {
      const response = await fetch(`/admin/pages/${this.getPageId()}/components/${componentId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.getCSRFToken()
        }
      })
      
      if (response.ok) {
        // Remove the item from the reorder list
        const item = event.currentTarget.closest('.reorder-item')
        item.remove()
        this.updateReorderPositions()
      } else {
        throw new Error('Failed to remove component')
      }
    } catch (error) {
      console.error('Error removing component:', error)
      alert('Failed to remove component. Please try again.')
    }
  }
  
  async saveOrder() {
    const items = this.reorderListTarget.querySelectorAll('.reorder-item')
    const positions = Array.from(items).map((item, index) => ({
      component_id: item.dataset.componentId,
      position: index
    }))
    
    try {
      const response = await fetch(`/admin/pages/${this.getPageId()}/components/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken()
        },
        body: JSON.stringify({ positions: positions })
      })
      
      if (response.ok) {
        // Close modal and reload page
        const modal = bootstrap.Modal.getInstance(this.element)
        modal.hide()
        window.location.reload()
      } else {
        throw new Error('Failed to save order')
      }
    } catch (error) {
      console.error('Error saving order:', error)
      alert('Failed to save order. Please try again.')
    }
  }
  
  updateReorderPositions() {
    const items = this.reorderListTarget.querySelectorAll('.reorder-item')
    items.forEach((item, index) => {
      const badge = item.querySelector('.badge')
      if (badge) {
        badge.textContent = index + 1
      }
      item.dataset.position = index
    })
  }
  
  // Helper methods
  getPageId() {
    // Extract page ID from URL or data attribute
    const pathParts = window.location.pathname.split('/')
    return pathParts[pathParts.indexOf('pages') + 1]
  }
  
  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]').getAttribute('content')
  }
} 